import React, { useCallback, useEffect, useState, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { DocumentUploadCard } from "../ui/DocumentUploadCard";
import { DocumentTypeSelector } from "../ui/DocumentTypeSelector";
import { UploadedFiles } from "../UploadedFiles";
import { InvalidRequirementReason } from "../InvalidRequirementReason";
import { CancelToReview } from "../CancelToReview";
import { FormValue, Status } from "../../../redux/interfaces/folder.types";
import { DocumentExamples } from "./DocumentExamples";
import {
  proofOfIncomePFTypes,
  proofOfIncomePMTypes,
  proofOfIncomeTypesPFMap,
  proofOfIncomeTypesPMMap,
  labelProofOfIncomeOptions,
} from "./fixtures/proofOfIncomeTypes";
import { toast } from "sonner";
import { getSubfoldersMapByIdToSlug } from "../../../utils/subfolders/subfoldersMapByIdToSlug";
import { uploadFileRequirement } from "../../../utils/vault/uploadFileRequirement";
import { useUploadFileToVault } from "../../../hooks/vault/mutations/useUploadFileToVault";
import { useUpdateVaultFormValues } from "../../../hooks/vault/mutations/useUpdateVaultFormValues";
import { Plus, X } from "lucide-react";
import { Button } from "../../ui/button";
import { cn } from "../../../lib/utils";
import type { UploadFile } from "antd/es/upload/interface";
import {
  getExistingProofOfIncomeFilesCount,
  getProofOfIncomeRequiredFileCount,
  getRequiredNewProofOfIncomeSlots,
} from "../../../utils/vault/proofOfIncomeFileCount";
import {
  BANK_STATEMENT_MAX_FILE_SIZE_MB,
  exceedsUploadTotalBytes,
  formatUploadLimit,
  SIMPLE_DOCUMENT_MAX_FILE_SIZE_MB,
  TAX_DECLARATION_MAX_FILE_SIZE_MB,
} from "../../../utils/vault/verificationUploadLimits";
import { UploadProgress } from "../ui/UploadProgress";
import { getDocumentUploadPolicy } from "../../../constants/uploadPolicies";

interface Props {
  type: "tenant" | "company" | "guarantor";
  onRegisterSubmit?: (submitFn: (() => Promise<boolean>) | null) => void;
}

export const ProofOfIncome: React.FC<Props> = ({ type, onRegisterSubmit }) => {
  const [documentType, setDocumentType] = useState<string | undefined>(
    undefined,
  );
  const [fileSlots, setFileSlots] = useState<UploadFile[][]>([[]]);

  const dispatch = useAppDispatch();
  const label = labelProofOfIncomeOptions[type];

  const { activeRequirement, activeVault, activeTab } = useAppSelector(
    (state) => state.vault,
  );
  const uploadMutation = useUploadFileToVault(activeVault?.id);
  const updateFormValuesMutation = useUpdateVaultFormValues(activeVault?.id);
  const uploadPolicy = getDocumentUploadPolicy({
    requirementSlug: activeRequirement?.slug,
    documentType,
    requirementStatus: activeRequirement?.status,
  });
  const savedDocumentType =
    activeRequirement?.form_values?.find(
      (item: FormValue) => item.key === "document_type",
    )?.value || undefined;
  const isInvalidRequirement = activeRequirement?.status === Status.Invalid;
  const currentEvidenceFiles =
    activeRequirement?.files?.filter(
      (file) => !file.is_verification_reference,
    ) ?? [];
  const existingFilesCount = currentEvidenceFiles.length;
  const existingFilesForSelectedType = getExistingProofOfIncomeFilesCount({
    existingFilesCount,
    savedDocumentType,
    selectedDocumentType: documentType,
  });
  const referenceFilesCount =
    activeRequirement?.files?.filter((file) => file.is_verification_reference)
      .length ?? 0;

  const enabledInputs =
    activeRequirement?.status === Status.PendingUpload || isInvalidRequirement;

  // Determine if PF or PM based on subfolder
  const subfolderMap = getSubfoldersMapByIdToSlug(activeVault);
  const isSubfolderPF = activeRequirement?.subfolder
    ? subfolderMap[activeRequirement.subfolder]?.includes("pf")
    : false;

  const proofOfIncomeTypes = isSubfolderPF
    ? proofOfIncomePFTypes
    : proofOfIncomePMTypes;
  const proofOfIncomeTypesMap = isSubfolderPF
    ? proofOfIncomeTypesPFMap
    : proofOfIncomeTypesPMMap;

  const nominaExample = {
    path: "/documents/comprobante-ingresos-2.jpeg",
    description: "Recibo de nómina",
  };

  const documentExamples = [
    {
      path: "/documents/comprobante-ingresos-1.jpeg",
      description: "Estado de cuenta",
    },
    {
      path: "/documents/declaracion_impuestos.png",
      description: "Declaración de impuestos",
    },
    ...(isSubfolderPF ? [nominaExample] : []),
  ];

  const getInitialSlots = useCallback(
    (type: string, existingCount = 0): UploadFile[][] => {
      const submitsBankStatementCollection = Boolean(
        (activeRequirement?.status === Status.PendingUpload ||
          activeRequirement?.status === Status.PendingReview) &&
          type === "bank_statement" &&
          existingCount > 0,
      );
      const requiredNewSlots = submitsBankStatementCollection
        ? getRequiredNewProofOfIncomeSlots({
            documentType: type,
            existingFilesCount: existingCount,
          })
        : isInvalidRequirement
          ? 1
          : getRequiredNewProofOfIncomeSlots({
              documentType: type,
              existingFilesCount: 0,
            });
      return Array.from({ length: requiredNewSlots }, () => []);
    },
    [activeRequirement?.status, isInvalidRequirement],
  );

  const initFormValues = useCallback(() => {
    if (!activeRequirement?.form_values) return;

    const documentTypeValue = savedDocumentType;

    setDocumentType(documentTypeValue);
    if (documentTypeValue) {
      setFileSlots(getInitialSlots(documentTypeValue, existingFilesCount));
    }
  }, [
    activeRequirement?.form_values,
    existingFilesCount,
    getInitialSlots,
    savedDocumentType,
  ]);

  useEffect(() => {
    setFileSlots([[]]);
    initFormValues();
  }, [activeRequirement?.id, initFormValues]);

  const handleDocumentTypeChange = (newType: string) => {
    if (newType !== documentType) {
      const existingCountForNewType = getExistingProofOfIncomeFilesCount({
        existingFilesCount,
        savedDocumentType,
        selectedDocumentType: newType,
      });
      setFileSlots(getInitialSlots(newType, existingCountForNewType));
    }
    setDocumentType(newType);
  };

  const handleFileChange = (index: number, files: UploadFile[]) => {
    setFileSlots((prev) => {
      const newSlots = [...prev];
      newSlots[index] = files;
      return newSlots;
    });
  };

  const addFileSlot = () => {
    if (fileSlots.length < maxNewFilesPerSubmission) {
      setFileSlots((prev) => [...prev, []]);
    }
  };

  const requiredFileCount = getProofOfIncomeRequiredFileCount(documentType);
  const submitsBankStatementCollection = Boolean(
    (activeRequirement?.status === Status.PendingUpload ||
      activeRequirement?.status === Status.PendingReview) &&
      documentType === "bank_statement" &&
      existingFilesForSelectedType > 0,
  );
  const minimumNewFiles = submitsBankStatementCollection
    ? getRequiredNewProofOfIncomeSlots({
        documentType,
        existingFilesCount: existingFilesForSelectedType,
      })
    : uploadPolicy.min_files;
  const minSlots = minimumNewFiles;

  const removeFileSlot = (index: number) => {
    if (fileSlots.length > minSlots) {
      setFileSlots((prev) => prev.filter((_, i) => i !== index));
    }
  };

  const newFilesCount = fileSlots.filter((slot) => slot.length > 0).length;
  const totalAvailableFilesCount = existingFilesForSelectedType + newFilesCount;
  const filesCountedAgainstPolicy = submitsBankStatementCollection
    ? totalAvailableFilesCount
    : newFilesCount;
  const hasReferenceFiles = referenceFilesCount > 0;
  const maxNewFilesPerSubmission = Math.max(
    0,
    uploadPolicy.max_files -
      (submitsBankStatementCollection ? existingFilesForSelectedType : 0),
  );
  const canAddMore =
    fileSlots.length < maxNewFilesPerSubmission && enabledInputs;
  const maxFileSizeMB =
    documentType === "bank_statement"
      ? BANK_STATEMENT_MAX_FILE_SIZE_MB
      : documentType === "tax_declaration"
        ? TAX_DECLARATION_MAX_FILE_SIZE_MB
        : SIMPLE_DOCUMENT_MAX_FILE_SIZE_MB;

  const handleSubmit = async (): Promise<boolean> => {
    // Collect all files from all slots
    const formFiles = fileSlots
      .flat()
      .map((file) => file.originFileObj)
      .filter(Boolean);

    if (
      (isInvalidRequirement ||
        (submitsBankStatementCollection && minimumNewFiles > 0)) &&
      formFiles.length === 0
    ) {
      toast.error(
        "Debes subir al menos un archivo nuevo antes de reenviar este requisito",
      );
      return false;
    }

    if (!documentType || filesCountedAgainstPolicy === 0) {
      toast.error("Por favor selecciona un tipo y sube al menos un archivo");
      return false;
    }

    const filesCountedAgainstTotal = submitsBankStatementCollection
      ? [
          ...currentEvidenceFiles.map((file) => ({
            size: file.size_bytes ?? 0,
          })),
          ...formFiles,
        ]
      : formFiles;
    if (
      exceedsUploadTotalBytes(
        filesCountedAgainstTotal,
        uploadPolicy.max_total_bytes,
      )
    ) {
      toast.error(
        `El total de archivos no puede exceder ${formatUploadLimit(uploadPolicy.max_total_bytes)}`,
      );
      return false;
    }
    if (filesCountedAgainstPolicy > uploadPolicy.max_files) {
      toast.error(
        `Este tipo de documento permite máximo ${uploadPolicy.max_files} archivo${uploadPolicy.max_files === 1 ? "" : "s"}.`,
      );
      return false;
    }
    if (
      filesCountedAgainstPolicy <
      (submitsBankStatementCollection
        ? requiredFileCount
        : uploadPolicy.min_files)
    ) {
      toast.error(
        `Debes tener al menos ${requiredFileCount} ${
          documentType === "bank_statement"
            ? "estados de cuenta bancarios"
            : "comprobante de ingresos"
        } cargado${requiredFileCount > 1 ? "s" : ""}`,
      );
      return false;
    }

    const documentMap: Record<string, string> = {
      document_type: documentType,
    };

    const documentTypeKey = "proof_of_income";
    const requirementFiles = activeRequirement?.files || [];
    // formNFiles must match maxAllowedFiles[documentType] (which is 1).
    // The old Ant Design code counted form field names (always 1), not individual files.
    // uploadFileToVault handles sending all files in a single request.
    const formNFiles = 1;
    const successMessage =
      formFiles.length > 0
        ? "Comprobante de ingresos subido correctamente."
        : "Comprobante de ingresos enviado a revisión.";

    const newFormValues: FormValue[] | undefined =
      activeRequirement?.form_values?.map((formItem) => ({
        ...formItem,
        value: documentMap[formItem.key],
      }));

    // Create a toast-based messageApi compatible object
    const messageApi = {
      success: (msg: string) => toast.success(msg),
      error: (msg: string) => toast.error(msg),
    };

    try {
      await uploadFileRequirement({
        uploadMutateAsync: uploadMutation.mutateAsync,
        updateFormValuesMutateAsync: updateFormValuesMutation.mutateAsync,
        dispatch,
        requirementFiles,
        documentType: documentTypeKey,
        uploadPolicyDocumentType: documentType,
        formNFiles,
        formFiles,
        formValues: {},
        activeRequirement,
        activeVault,
        activeTab,
        successMessage,
        reqFormValues: newFormValues,
        messageApi,
        preserveCurrentEvidence: submitsBankStatementCollection,
        minimumUploadFiles: minimumNewFiles,
      });
      setFileSlots([[]]);
      return true;
    } catch {
      return false;
    }
  };

  // Register submit function with parent for external triggering
  const submitFnRef = useRef<(() => Promise<boolean>) | undefined>(undefined);
  submitFnRef.current = handleSubmit;

  useEffect(() => {
    if (!onRegisterSubmit) return;
    const fn = async () => submitFnRef.current?.() ?? false;
    onRegisterSubmit(fn);
    return () => onRegisterSubmit(null);
  }, [onRegisterSubmit]);

  return (
    <div className="w-full">
      {/* Header */}
      <div className="mb-8">
        <p className="text-gray-600">
          Comprobantes de ingresos de los últimos 3 meses del{" "}
          <span className="font-semibold text-gray-900">{label}</span>.
        </p>
      </div>

      {/* Invalid Requirement Reason — shown at the top so the rejection
          message is visible before the user scrolls through the form. */}
      {activeRequirement && (
        <InvalidRequirementReason requirement={activeRequirement} />
      )}

      {/* Two column layout - stack on mobile, 40/60 split on md+ */}
      <div className="grid grid-cols-1 md:grid-cols-[2fr_3fr] gap-6 md:gap-8">
        {/* Left Column - Document Type Selection */}
        <div className="space-y-6 md:sticky md:top-0 md:self-start">
          <DocumentTypeSelector
            title="Tipo de comprobante"
            options={proofOfIncomeTypes}
            value={documentType}
            onChange={handleDocumentTypeChange}
            disabled={!enabledInputs}
          />

          {/* Tips */}
          <div className="space-y-3">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              Requisitos
            </p>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-start gap-2">
                <span className="mt-1.5 w-1 h-1 rounded-full bg-gray-400 flex-shrink-0" />
                NO cartas emitidas por empresa
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-1.5 w-1 h-1 rounded-full bg-gray-400 flex-shrink-0" />
                Documentación clara y completa
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-1.5 w-1 h-1 rounded-full bg-gray-400 flex-shrink-0" />
                No borrar o tachar información
              </li>
            </ul>
            <DocumentExamples urls={documentExamples} />
          </div>
        </div>

        {/* Right Column - File Upload */}
        <div className="space-y-6">
          {documentType && (
            <div className="space-y-4">
              {/* File slots */}
              {fileSlots.map((files, index) => (
                <div key={index} className="relative">
                  {/* Remove button for extra slots */}
                  {fileSlots.length > minSlots && enabledInputs && (
                    <button
                      type="button"
                      onClick={() => removeFileSlot(index)}
                      className={cn(
                        "absolute -top-2 -right-2 z-10",
                        "flex h-6 w-6 items-center justify-center",
                        "rounded-full bg-gray-100 border border-gray-200",
                        "text-gray-500 hover:text-red-600 hover:bg-red-50 hover:border-red-200",
                        "transition-colors",
                      )}
                      title="Eliminar"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}

                  {/* Slot label */}
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Documento {index + 1}
                    </span>
                  </div>

                  <DocumentUploadCard
                    name={`proof_of_income_${index}`}
                    label={
                      proofOfIncomeTypesMap[documentType] ||
                      "Comprobante de ingresos"
                    }
                    type="both"
                    enableCropper={false}
                    maxSizeMB={maxFileSizeMB}
                    onFileChange={(newFiles) =>
                      handleFileChange(index, newFiles)
                    }
                    value={files}
                  />
                </div>
              ))}

              {/* Add more button */}
              {canAddMore && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addFileSlot}
                  className="w-full gap-2"
                >
                  <Plus className="h-4 w-4" />
                  Agregar otro documento
                </Button>
              )}

              {/* Files counter */}
              {(filesCountedAgainstPolicy > 0 || hasReferenceFiles) && (
                <p className="text-xs text-gray-500 text-center">
                  {hasReferenceFiles
                    ? `${referenceFilesCount} archivo${
                        referenceFilesCount === 1 ? "" : "s"
                      } de referencia · ${filesCountedAgainstPolicy} documento${
                        filesCountedAgainstPolicy === 1 ? "" : "s"
                      } ${submitsBankStatementCollection ? "disponible" : "nuevo"}${
                        filesCountedAgainstPolicy === 1 ? "" : "s"
                      }${
                        documentType === "bank_statement"
                          ? `, mínimo ${requiredFileCount}`
                          : ""
                      }`
                    : documentType === "bank_statement"
                      ? `${filesCountedAgainstPolicy} documento${
                          filesCountedAgainstPolicy === 1 ? "" : "s"
                        } disponible${
                          filesCountedAgainstPolicy === 1 ? "" : "s"
                        }, mínimo ${requiredFileCount}`
                      : `${filesCountedAgainstPolicy} documento${
                          filesCountedAgainstPolicy === 1 ? "" : "s"
                        } cargado${filesCountedAgainstPolicy === 1 ? "" : "s"}`}
                </p>
              )}
            </div>
          )}

          <UploadProgress
            status={uploadMutation.status}
            progress={uploadMutation.uploadProgress}
            transport={uploadMutation.uploadTransport}
          />

          {!documentType && enabledInputs && (
            <div className="flex items-center justify-center min-h-[180px] rounded-lg border-2 border-dashed border-gray-200 bg-gray-50">
              <p className="text-sm text-gray-500">
                Selecciona un tipo de comprobante para continuar
              </p>
            </div>
          )}

          {/* Uploaded Files */}
          <UploadedFiles />

          {/* Cancel to review (when already submitted) */}
          {!enabledInputs &&
            activeRequirement?.status === Status.PendingReview && (
              <CancelToReview requirement={activeRequirement} />
            )}
        </div>
      </div>
    </div>
  );
};
